import { useState, useEffect, useCallback } from "react";
import {
  hasMetaMask,
  requestAccounts,
  getAccounts,
  getChainId,
  switchToAmoy,
  erc20BalanceOf,
  formatTokenBalance,
} from "@/lib/wallet";
import { POLYGON_AMOY_CHAIN_ID, TOKEN_ADDRESSES } from "@/lib/wagmi";

export interface WalletState {
  address: string | null;
  chainId: number;
  isConnected: boolean;
  isConnecting: boolean;
  wrongNetwork: boolean;
  gloBalance: string;
  haloBalance: string;
  hasMetaMask: boolean;
  connect: () => Promise<void>;
  disconnect: () => void;
  switchNetwork: () => Promise<void>;
}

export function useWallet(): WalletState {
  const [address, setAddress]         = useState<string | null>(null);
  const [chainId, setChainId]         = useState<number>(0);
  const [isConnecting, setConnecting] = useState(false);
  const [gloBalance, setGloBalance]   = useState("—");
  const [haloBalance, setHaloBalance] = useState("—");

  const isConnected  = !!address;
  const wrongNetwork = isConnected && chainId !== POLYGON_AMOY_CHAIN_ID;

  const refreshBalances = useCallback(async (addr: string) => {
    if (!TOKEN_ADDRESSES.GLO && !TOKEN_ADDRESSES.HALO) return;
    if (TOKEN_ADDRESSES.GLO) {
      const raw = await erc20BalanceOf(TOKEN_ADDRESSES.GLO, addr);
      setGloBalance(formatTokenBalance(raw, 9));
    }
    if (TOKEN_ADDRESSES.HALO) {
      const raw = await erc20BalanceOf(TOKEN_ADDRESSES.HALO, addr);
      setHaloBalance(formatTokenBalance(raw, 18));
    }
  }, []);

  const connect = useCallback(async () => {
    if (!hasMetaMask()) return;
    setConnecting(true);
    try {
      const accounts = await requestAccounts();
      if (accounts[0]) {
        const addr = accounts[0];
        setAddress(addr);
        const id = await getChainId();
        setChainId(id);
        await refreshBalances(addr);
      }
    } finally {
      setConnecting(false);
    }
  }, [refreshBalances]);

  const disconnect = useCallback(() => {
    setAddress(null);
    setChainId(0);
    setGloBalance("—");
    setHaloBalance("—");
  }, []);

  const switchNetwork = useCallback(async () => {
    await switchToAmoy();
    const id = await getChainId();
    setChainId(id);
  }, []);

  // Re-check on mount in case already connected
  useEffect(() => {
    (async () => {
      const accounts = await getAccounts();
      if (accounts[0]) {
        setAddress(accounts[0]);
        const id = await getChainId();
        setChainId(id);
        await refreshBalances(accounts[0]);
      }
    })();
  }, [refreshBalances]);

  // Listen for account + chain changes
  useEffect(() => {
    if (!window.ethereum) return;
    const onAccounts = (accounts: unknown) => {
      const list = accounts as string[];
      if (list[0]) {
        setAddress(list[0]);
        refreshBalances(list[0]);
      } else {
        setAddress(null);
      }
    };
    const onChain = (id: unknown) => {
      setChainId(parseInt(id as string, 16));
    };
    window.ethereum.on("accountsChanged", onAccounts);
    window.ethereum.on("chainChanged", onChain);
    return () => {
      window.ethereum?.removeListener("accountsChanged", onAccounts);
      window.ethereum?.removeListener("chainChanged", onChain);
    };
  }, [refreshBalances]);

  // Refresh balances every 30s when connected
  useEffect(() => {
    if (!address) return;
    const id = setInterval(() => refreshBalances(address), 30_000);
    return () => clearInterval(id);
  }, [address, refreshBalances]);

  return {
    address,
    chainId,
    isConnected,
    isConnecting,
    wrongNetwork,
    gloBalance,
    haloBalance,
    hasMetaMask: hasMetaMask(),
    connect,
    disconnect,
    switchNetwork,
  };
}
